import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CLIENT_GROUPED_QUERY_KEY } from '@/app/(main)/client-detail/hooks/useGetClientGroupedQuery';
import { CLIENT_CREATE_QUERY_KEY, RELATED_TRAVELERS_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { useDeleteLoyaltyProgramMutation } from '@/app/(main)/clients/create/hooks/useDeleteLoyaltyProgramMutation';
import { useEditClientMutation } from '@/app/(main)/clients/hooks/useEditClientMutation';
import type { CreateClientFormData } from '@/types/clientsApiTypes';
import type { LoyaltyProgramFormData } from '@/types/clientsApiTypes';
import { nullableNumber, nullableString } from '@/utils/validations';
import { CLIENT_DETAILS_QUERY_KEY } from '../../../consts/client-detail';
import { EditLoyaltyProgramsForm } from '../../EditSectionModals/components/EditLoyaltyProgramsForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { getLoyaltyProgramsPayload } from '../../EditSectionModals/utils/getLoyaltyProgramsPayload';
import type { LoyaltyProgramsFormValues, LoyaltyProgramsPayload } from './types';
import { buildLoyaltyNumbersPatchBody } from './utils/buildLoyaltyNumbersPatchBody';

function isSkippableEmptyLoyaltyRow(row: LoyaltyProgramFormData): boolean {
  const isUnsaved = row.lp_id == null || row.lp_id === 0;
  if (!isUnsaved) return false;
  const hasCategory = row.category_id != null && row.category_id !== 0;
  return !hasCategory;
}

const schema = yup.object({
  primary_passenger: yup.object().shape({
    loyalty_numbers: yup
      .array()
      .of(
        yup.object({
          lp_id: nullableNumber().defined(),
          category_id: nullableNumber().defined(),
          number: nullableString().defined(),
          loyalty_program: nullableString().defined(),
          frequent_flyer_number: nullableString().defined(),
          is_legacy: yup.boolean().defined(),
          note: nullableString().defined(),
        })
      )
      .required()
      .default([]),
  }),
});

export type LoyaltyProgramsSectionFormValues = yup.InferType<typeof schema>;

function useLoyaltyProgramsOnSave() {
  const editMutation = useEditClientMutation();
  const deleteMutation = useDeleteLoyaltyProgramMutation();
  const queryClient = useQueryClient();

  return async (payload: LoyaltyProgramsPayload) => {
    const { clientId, items } = payload;

    const deletes = items.filter((i) => i.action === 'delete');
    const patchItems = items.filter((i) => i.action === 'create' || i.action === 'update');

    for (const item of deletes) {
      if (item.id != null) {
        await deleteMutation.mutateAsync({ clientId, lpId: item.id });
      }
    }

    if (patchItems.length > 0) {
      const loyaltyNumbers = buildLoyaltyNumbersPatchBody(items);
      await editMutation.mutateAsync(
        {
          data: { loyalty_numbers: loyaltyNumbers as never },
          clientId,
        },
        {
          onSuccess: () => {
            toast.success('Client updated successfully');
            queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [CLIENT_GROUPED_QUERY_KEY] });
          },
        }
      );
    } else if (deletes.length > 0) {
      toast.success('Client updated successfully');
      queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [CLIENT_GROUPED_QUERY_KEY] });
    }
  };
}

function mapValuesToPayload(
  values: LoyaltyProgramsFormValues,
  client: CreateClientFormData | null | undefined
): LoyaltyProgramsPayload {
  const clientId = client?.primary_passenger?.client?.client_id ?? 0;
  const loyaltyNumbers = values.primary_passenger?.loyalty_numbers ?? [];
  const filteredLoyaltyNumbers = loyaltyNumbers.filter((row) => !isSkippableEmptyLoyaltyRow(row));
  const items = getLoyaltyProgramsPayload(
    {
      ...values,
      primary_passenger: {
        ...values.primary_passenger,
        loyalty_numbers: filteredLoyaltyNumbers,
      },
    },
    client
  );
  return { clientId, items };
}

export const loyaltyProgramsSection = defineSection<
  (typeof ModalKeys)['loyalty_programs'],
  CreateClientFormData,
  typeof schema,
  LoyaltyProgramsPayload
>({
  key: ModalKeys.loyalty_programs,
  title: 'Loyalty programs',
  subtitle: 'Airline and hotel loyalty memberships associated with the client.',
  schema,
  mapClientToValues: (c) => ({
    primary_passenger: {
      loyalty_numbers: c.primary_passenger?.loyalty_numbers ?? [],
    },
  }),
  mapValuesToPayload,
  useOnSave: useLoyaltyProgramsOnSave,
  Fields: EditLoyaltyProgramsForm,
});
